import { Cartesian2, Cartesian3, Color, CallbackProperty, Viewer, Matrix4 } from "cesium";

export class CesiumSnap {
    private viewer: Viewer;
    private _snapIndicatorEntity: any = undefined;
    private _orangeSquareImage: string = "";
    private _greenSquareImage: string = "";
    private _snapLinePositions: Cartesian3[] = [];
    private _snapLineShow: boolean = false;

    private _lastSnappedPosition: Cartesian3 | undefined = undefined;
    private _isSnapped: boolean = false;
    private _lastMousePosition: Cartesian2 | undefined = undefined;
    private _lastCameraPosition = new Cartesian3();
    private _lastCameraDirection = new Cartesian3();
    private _postRenderListener: any = undefined;

    private _lastSnapTime = 0;
    private _throttleTimeout: any = undefined;

    constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    get isSnapped(): boolean {
        return this._isSnapped;
    }

    get lastSnappedPosition(): Cartesian3 | undefined {
        return this._lastSnappedPosition;
    }

    enable = () => {
        if (!this._postRenderListener) {
            this._lastMousePosition = undefined;
            this._isSnapped = false;
            this._lastSnappedPosition = undefined;

            Cartesian3.clone(this.viewer.camera.position, this._lastCameraPosition);
            Cartesian3.clone(this.viewer.camera.direction, this._lastCameraDirection);

            this._postRenderListener = this.viewer.scene.postRender.addEventListener(this.handlePostRender);
        }
    }

    disable = () => {
        this.hideSnapIndicator();
        if (this._postRenderListener) {
            this._postRenderListener();
            this._postRenderListener = undefined;
        }
        if (this._throttleTimeout) {
            clearTimeout(this._throttleTimeout);
            this._throttleTimeout = undefined;
        }
        this._lastMousePosition = undefined;
        this._isSnapped = false;
        this._lastSnappedPosition = undefined;
        this._lastSnapTime = 0;
    }

    private handlePostRender = () => {
        const camera = this.viewer.camera;
        if (!this._lastMousePosition) return;

        const currentPos = camera.position;
        const currentDir = camera.direction;

        if (!Cartesian3.equals(currentPos, this._lastCameraPosition) ||
            !Cartesian3.equals(currentDir, this._lastCameraDirection)) {

            Cartesian3.clone(currentPos, this._lastCameraPosition);
            Cartesian3.clone(currentDir, this._lastCameraDirection);

            this.updateSnapThrottled(this._lastMousePosition);
        }
    }

    updateSnapThrottled = (screenPosition: Cartesian2, onSnapUpdated?: (snappedPos: Cartesian3 | undefined) => void) => {
        this._lastMousePosition = screenPosition;

        const now = performance.now();
        const limit = 20;

        if (now - this._lastSnapTime >= limit) {
            this._lastSnapTime = now;
            if (this._throttleTimeout) {
                clearTimeout(this._throttleTimeout);
                this._throttleTimeout = undefined;
            }
            const snappedPos = this.updateSnap(screenPosition);
            if (onSnapUpdated) onSnapUpdated(snappedPos);
        } else {
            if (this._throttleTimeout) {
                clearTimeout(this._throttleTimeout);
            }
            this._throttleTimeout = setTimeout(() => {
                this._lastSnapTime = performance.now();
                this._throttleTimeout = undefined;
                const snappedPos = this.updateSnap(this._lastMousePosition || screenPosition);
                if (onSnapUpdated) onSnapUpdated(snappedPos);
            }, limit - (now - this._lastSnapTime));
        }
    }

    private updateSnap = (screenPosition: Cartesian2): Cartesian3 | undefined => {
        this._lastMousePosition = screenPosition;
        return this.getSnappedPosition(screenPosition);
    }

    private showSnapIndicator = (position: Cartesian3, isCorner: boolean, linePositions?: Cartesian3[]) => {
        if (!this._orangeSquareImage) {
            const canvas = document.createElement("canvas");
            canvas.width = 20;
            canvas.height = 20;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.strokeStyle = "rgba(255, 127, 0, 1.0)";
                ctx.lineWidth = 2.5;
                ctx.strokeRect(1.25, 1.25, 17.5, 17.5);
            }
            this._orangeSquareImage = canvas.toDataURL();
        }

        if (!this._greenSquareImage) {
            const canvas = document.createElement("canvas");
            canvas.width = 20;
            canvas.height = 20;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.strokeStyle = "rgba(0, 255, 127, 1.0)";
                ctx.lineWidth = 2.5;
                ctx.strokeRect(1.25, 1.25, 17.5, 17.5);
            }
            this._greenSquareImage = canvas.toDataURL();
        }

        const imageToUse = isCorner ? this._greenSquareImage : this._orangeSquareImage;

        if (linePositions && linePositions.length === 2) {
            this._snapLinePositions = linePositions;
            this._snapLineShow = true;
        } else {
            this._snapLinePositions = [];
            this._snapLineShow = false;
        }

        if (!this._snapIndicatorEntity) {
            this._snapIndicatorEntity = this.viewer.entities.add({
                name: "dpu-snap-indicator",
                position: position,
                billboard: {
                    image: imageToUse,
                    width: 20,
                    height: 20,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                    show: true
                },
                polyline: {
                    positions: new CallbackProperty(() => this._snapLinePositions, false),
                    width: 3.5,
                    material: Color.YELLOW,
                    disableDepthTestDistance: Number.POSITIVE_INFINITY,
                    show: new CallbackProperty(() => this._snapLineShow, false)
                } as any

            });
        } else {
            this._snapIndicatorEntity.position = position;
            if (this._snapIndicatorEntity.billboard) {
                this._snapIndicatorEntity.billboard.image = imageToUse;
                this._snapIndicatorEntity.billboard.show = true;
            }
            if (this._snapIndicatorEntity.point) {
                this._snapIndicatorEntity.point.show = false;
            }
            this._snapIndicatorEntity.show = true;
        }
        this.viewer.scene.requestRender();
    }

    hideSnapIndicator = () => {
        this._snapLinePositions = [];
        this._snapLineShow = false;
        if (this._snapIndicatorEntity) {
            this._snapIndicatorEntity.show = false;
        }
        this.viewer.scene.requestRender();
    }

    private getWorldPos = (sp: Cartesian2, allowGlobeFallback: boolean = false): Cartesian3 | undefined => {
        let p = this.viewer.scene.pickPosition(sp);
        if (!p && allowGlobeFallback) {
            const ray = this.viewer.camera.getPickRay(sp);
            if (ray) {
                p = this.viewer.scene.globe.pick(ray, this.viewer.scene) as Cartesian3;
            }
        }
        return p;
    }

    private getPointDepth = (p: Cartesian3): number => {
        return Cartesian3.distance(this.viewer.camera.position, p);
    }

    private smoothstep = (edge0: number, edge1: number, x: number): number => {
        const t = Math.max(0.0, Math.min(1.0, (x - edge0) / (edge1 - edge0)));
        return t * t * (3.0 - 2.0 * t);
    }

    getSnappedPosition = (screenPosition: Cartesian2): Cartesian3 | undefined => {
        const centerPicked = this.viewer.scene.pick(screenPosition);

        const pixelRatio = window.devicePixelRatio || 1.0;
        const step = 1.0 / pixelRatio;
        const G = 9;
        const half = 4;

        const positions: (Cartesian3 | undefined)[][] = Array.from({ length: G }, () => Array(G).fill(undefined));
        const depths: number[][] = Array.from({ length: G }, () => Array(G).fill(0.0));

        let depthRef = Infinity;
        let centerPos: Cartesian3 | undefined = undefined;

        for (let i = 0; i < G; i++) {
            for (let j = 0; j < G; j++) {
                const sx = screenPosition.x + (i - half) * step;
                const sy = screenPosition.y + (j - half) * step;
                const sp = new Cartesian2(sx, sy);
                const isCenter = (i === half && j === half);
                const p = this.getWorldPos(sp, isCenter);
                if (p) {
                    positions[i][j] = p;
                    const d = this.getPointDepth(p);
                    depths[i][j] = d;

                    if (d < depthRef) {
                        depthRef = d;
                    }
                    if (isCenter) {
                        centerPos = p;
                    }
                }
            }
        }

        if (depthRef === Infinity) return undefined;

        const normals: (Cartesian3 | undefined)[][] = Array.from({ length: G }, () => Array(G).fill(undefined));
        const threshold = Math.max(0.05, depthRef * 0.05);

        for (let i = 0; i < G - 1; i++) {
            for (let j = 0; j < G - 1; j++) {
                const c = positions[i][j];
                if (!c) continue;

                const r = positions[i + 1][j];
                const u = positions[i][j + 1];
                if (!r || !u) continue;

                const depth_c = depths[i][j];
                const depth_r = depths[i + 1][j];
                const depth_u = depths[i][j + 1];

                if (Math.abs(depth_r - depth_c) <= threshold && Math.abs(depth_u - depth_c) <= threshold) {
                    const dx = Cartesian3.subtract(r, c, new Cartesian3());
                    const dy = Cartesian3.subtract(u, c, new Cartesian3());
                    const n = Cartesian3.cross(dx, dy, new Cartesian3());
                    normals[i][j] = Cartesian3.normalize(n, new Cartesian3());
                }
            }
        }

        const getWeightedNormal = (pos_n: Cartesian3 | undefined, depth_n: number, n_c: Cartesian3, depth_c: number, n_raw: Cartesian3 | undefined): Cartesian3 => {
            if (!pos_n || depth_n <= 0.0 || !n_raw) return n_c;
            const absDiff = Math.abs(depth_c - depth_n);
            const edge0 = depth_c * 0.05;
            const edge1 = depth_c * 0.01;
            const weight = this.smoothstep(edge0, edge1, absDiff);
            return Cartesian3.lerp(n_c, n_raw, weight, new Cartesian3());
        };

        let maxScore = -1.0;
        let bestEdgeVal = 0.0;
        let bestI = half;
        let bestJ = half;

        for (let i = 1; i < G - 1; i++) {
            for (let j = 1; j < G - 1; j++) {
                const c = positions[i][j];
                if (!c) continue;

                const n_c = normals[i][j];
                if (!n_c) continue;

                const depth_c = depths[i][j];

                const n_r = (i + 1 < G - 1 && normals[i + 1][j]) ? getWeightedNormal(positions[i + 1][j], depths[i + 1][j], n_c, depth_c, normals[i + 1][j]) : n_c;
                const n_d = (j - 1 >= 1 && normals[i][j - 1]) ? getWeightedNormal(positions[i][j - 1], depths[i][j - 1], n_c, depth_c, normals[i][j - 1]) : n_c;

                const diffX = 1.0 - Math.max(0.0, Math.min(1.0, Cartesian3.dot(n_c, n_r)));
                const diffY = 1.0 - Math.max(0.0, Math.min(1.0, Cartesian3.dot(n_c, n_d)));
                const normalGrad = Math.max(diffX, diffY);

                const normalStrengthVal = 4.0;
                const eVal = Math.max(0.0, Math.min(1.0, normalGrad * normalStrengthVal * 3.0));
                const tVal = eVal * eVal;
                const normalEdge = tVal * tVal * (3.0 - 2.0 * tVal);

                let depthEdge = 0.0;
                const depthStrengthVal = 2.0;
                if (depthStrengthVal > 0.0) {
                    const dThreshold = 0.1 / depthStrengthVal;
                    const ed_l = depths[i - 1][j] > 0 ? depths[i - 1][j] : 100000.0;
                    const ed_r = depths[i + 1][j] > 0 ? depths[i + 1][j] : 100000.0;
                    const ed_t = depths[i][j + 1] > 0 ? depths[i][j + 1] : 100000.0;
                    const ed_d = depths[i][j - 1] > 0 ? depths[i][j - 1] : 100000.0;

                    let maxRatio = 0.0;
                    maxRatio = Math.max(maxRatio, ed_l / depth_c - 1.0);
                    maxRatio = Math.max(maxRatio, ed_r / depth_c - 1.0);
                    maxRatio = Math.max(maxRatio, ed_t / depth_c - 1.0);
                    maxRatio = Math.max(maxRatio, ed_d / depth_c - 1.0);

                    depthEdge = this.smoothstep(0.0, dThreshold * 0.3, maxRatio - dThreshold);
                }

                const edgeVal = Math.max(depthEdge, normalEdge);

                const dx = i - half;
                const dy = j - half;
                const distScreen = Math.sqrt(dx * dx + dy * dy);
                const distanceFactor = Math.exp(-(distScreen * distScreen) / 20.0);

                let score = edgeVal * distanceFactor;

                if (score > maxScore) {
                    maxScore = score;
                    bestEdgeVal = edgeVal;
                    bestI = i;
                    bestJ = j;
                }
            }
        }

        let resultPos = positions[bestI][bestJ] || centerPos;
        let isCorner = false;
        let isProjectedOnPlane = false;

        const primitive = centerPicked?.primitive || centerPicked?.tileset;
        if (primitive && primitive.clippingPlanes && primitive.clippingPlanes.enabled && primitive.clippingPlanes.length > 0) {
            const plane = primitive.clippingPlanes.get(0);
            if (plane) {
                let originMatrix = Matrix4.IDENTITY;
                const tilesetAny = primitive as any;
                if (tilesetAny._initialClippingPlanesOriginMatrix) {
                    originMatrix = tilesetAny._initialClippingPlanesOriginMatrix;
                } else if (tilesetAny.clippingPlanesOriginMatrix) {
                    originMatrix = tilesetAny.clippingPlanesOriginMatrix;
                } else if (primitive.root && primitive.root.computedTransform) {
                    originMatrix = primitive.root.computedTransform;
                } else if (primitive.modelMatrix) {
                    originMatrix = primitive.modelMatrix;
                }

                const inverseMatrix = Matrix4.inverse(originMatrix, new Matrix4());
                if (inverseMatrix) {
                    const localPos = Matrix4.multiplyByPoint(inverseMatrix, resultPos as Cartesian3, new Cartesian3());
                    const distToPlane = Cartesian3.dot(plane.normal, localPos) + plane.distance;

                    const expectedPixelSize = depthRef * 0.0012;
                    const snapThreshold = Math.max(0.05, expectedPixelSize * 25.0);

                    if (Math.abs(distToPlane) <= snapThreshold) {
                        let shouldProject = true;
                        const worldNormal = normals[bestI]?.[bestJ];
                        if (worldNormal) {
                            const localNormal = Matrix4.multiplyByPointAsVector(inverseMatrix, worldNormal, new Cartesian3());
                            Cartesian3.normalize(localNormal, localNormal);
                            const cosAngle = Math.abs(Cartesian3.dot(localNormal, plane.normal));

                            if (cosAngle > 0.9) {
                                shouldProject = false;
                            }
                        }

                        if (shouldProject) {
                            const projLocal = Cartesian3.subtract(
                                localPos,
                                Cartesian3.multiplyByScalar(plane.normal, distToPlane, new Cartesian3()),
                                new Cartesian3()
                            );
                            resultPos = Matrix4.multiplyByPoint(originMatrix, projLocal, new Cartesian3());
                            isProjectedOnPlane = true;
                        }
                    }
                }
            }
        }

        const hasSnapped = bestEdgeVal > 0.05;

        if (hasSnapped || isProjectedOnPlane) {
            this._isSnapped = true;
            this._lastSnappedPosition = resultPos as Cartesian3;
            this.showSnapIndicator(resultPos as Cartesian3, isCorner);
            return resultPos;
        } else {
            this._isSnapped = false;
            this._lastSnappedPosition = undefined;
            this.hideSnapIndicator();
            return centerPos;
        }
    }
}
